import { countLeafColumns } from "src/generator/PortraitOrientation/PortraitSection/PortraitSectionHeaders/generateHeaders.utils";
import { type ICol } from "src/interfaces";
import { type ILogbookCharts } from "src/redux/slices/entriesGeneratorSlice";

// Mirrors the unwrap Section.tsx/Report.tsx apply before rendering/exporting:
// section.headers can arrive wrapped in an extra array level.
export const getWorkingHeaders = (headers: unknown): ICol[] => {
  if (!headers) return [];
  return Array.isArray((headers as any)[0])
    ? (headers as any)[0]
    : (headers as ICol[]);
};

// Mirrors the same normalization Report.tsx applies to section.rows before
// export: rows can arrive double-wrapped (sub-row groups) and need flattening.
export const getNormalizedRows = (rows: unknown): any[] => {
  if (!rows || !Array.isArray(rows)) return [];
  const isSubRows = (rows as any)[0]?.[0] && Array.isArray((rows as any)[0][0]);
  return isSubRows ? (rows as any[]).flat() : (rows as any[]);
};

// Mirrors Row.tsx:49 — a row is either { cols: ICol[] } or a bare ICol[].
export const getRowCols = (row: unknown): ICol[] => {
  if (!row) return [];
  return "cols" in (row as any) ? (row as any).cols : (row as ICol[]);
};

// Resolves a leaf column's name to its position in the leaf-column-ordered
// row cell array, walking nested group headers the same way
// generateHeaders.utils.ts's countLeafColumns/calculateHeaderDepth do, since
// a group header occupies one slot in `headers` but spans N leaf columns.
export const findLeafColumnIndex = (
  headers: ICol[],
  colName: string,
): number => {
  let offset = 0;

  for (const header of headers) {
    if (header.type === "space") {
      if (header.name === colName) return offset;
      offset += 1;
      continue;
    }

    if (header.cols && header.cols.length > 0) {
      const nested = findLeafColumnIndex(header.cols, colName);
      if (nested !== -1) return offset + nested;
      offset += countLeafColumns(header.cols);
      continue;
    }

    if (header.name === colName) return offset;
    offset += 1;
  }

  return -1;
};

export const findLeafColumn = (
  headers: ICol[],
  colName: string,
): ICol | undefined => {
  for (const header of headers) {
    if (header.type === "space") {
      if (header.name === colName) return header;
      continue;
    }

    if (header.cols && header.cols.length > 0) {
      const nested = findLeafColumn(header.cols, colName);
      if (nested) return nested;
      continue;
    }

    if (header.name === colName) return header;
  }

  return undefined;
};

// Mirrors Row.tsx:79-88's colSpan accounting, so a leaf index still resolves
// to the correct cell when earlier row cells span more than one column.
export const getCellAtLeafIndex = (
  rowCols: ICol[],
  leafIndex: number,
): ICol | undefined => {
  let offset = 0;

  for (const cell of rowCols) {
    const cellAny = cell as any;
    const span = cellAny.colspan
      ? cellAny.colspan
      : cellAny.span
        ? 1 + cellAny.span
        : !cellAny.full
          ? 1
          : 999;

    if (leafIndex >= offset && leafIndex < offset + span) return cell;
    offset += span;
  }

  return undefined;
};

// Mirrors Report.utils.tsx's getColValue default case.
export const extractCellValue = (
  col?: ICol,
): string | number | undefined => {
  if (!col) return undefined;
  const raw = col.value;
  const value = raw && typeof raw === "object" ? raw.value : raw;
  if (value === undefined || value === null || value === "") return undefined;
  return value;
};

export const mergeChartSettings = (
  rebuilt: ILogbookCharts[],
  prev: ILogbookCharts[],
): ILogbookCharts[] =>
  rebuilt.map(log => {
    const prevLog = prev.find(p => p.lbid === log.lbid);
    return {
      ...log,
      data: log.data.flatMap(freshChart => {
        const prevCharts =
          prevLog?.data.filter(
            p =>
              p.name === freshChart.name &&
              p.sectionIndex === freshChart.sectionIndex,
          ) ?? [];
        if (!prevCharts.length) return [freshChart];

        return prevCharts.map(prevChart => {
          const { valueSource } = prevChart;
          const sumColumnGone =
            valueSource?.kind === "sum" &&
            !log.data.some(
              c =>
                c.name === valueSource.column &&
                c.sectionIndex === freshChart.sectionIndex,
            );
          return {
            ...freshChart,
            id: prevChart.id,
            type: prevChart.type,
            trendline: prevChart.trendline,
            sortBy: prevChart.sortBy,
            sortDirection: prevChart.sortDirection,
            valueSource: sumColumnGone ? { kind: "count" as const } : valueSource,
          };
        });
      }),
    };
  });
